import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Phone, 
  Calendar, 
  Clock, 
  Video, 
  MessageSquare,
  ChevronRight,
  AlertTriangle,
  Stethoscope,
  MapPin,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface GuidedNextStepsProps {
  urgency: 'immediate' | 'urgent' | 'standard' | 'non-urgent';
  estimatedWaitTime?: string;
  nearestFacility?: {
    name: string;
    distance: string;
    address: string;
    phone: string;
  };
  className?: string;
}

export function GuidedNextSteps({ 
  urgency, 
  estimatedWaitTime,
  nearestFacility,
  className 
}: GuidedNextStepsProps) {
  const navigate = useNavigate();

  // Define guidance content based on urgency
  const guidanceContent = {
    immediate: {
      title: 'Emergency Assistance Required',
      description: 'Based on your symptoms, immediate medical attention is needed.',
      primaryAction: {
        label: 'Call Emergency Services',
        icon: <Phone className="w-5 h-5" />,
        action: () => window.location.href = 'tel:911',
        variant: 'destructive' as const
      },
      secondaryAction: {
        label: 'Find Nearest ER',
        icon: <MapPin className="w-5 h-5" />,
        action: () => navigate('/patient/emergency-locations'),
        variant: 'outline' as const
      },
      additionalInfo: 'If you are unable to travel safely, please call emergency services immediately.',
      steps: [
        'Call emergency services (911)',
        'Inform them about your symptoms',
        'Follow their instructions carefully',
        'Have someone accompany you if possible'
      ]
    },
    urgent: {
      title: 'Urgent Care Needed',
      description: 'Your symptoms require prompt medical attention within the next few hours.',
      primaryAction: {
        label: 'Connect with Tele-Doctor',
        icon: <Video className="w-5 h-5" />,
        action: () => navigate('/patient/tele-triage'),
        variant: 'default' as const
      },
      secondaryAction: {
        label: 'Find Urgent Care',
        icon: <MapPin className="w-5 h-5" />,
        action: () => navigate('/patient/urgent-care-locations'),
        variant: 'outline' as const
      },
      additionalInfo: 'Urgent care is recommended within 2-4 hours. Tele-doctors are available now.',
      steps: [
        'Connect with a tele-doctor for immediate guidance',
        'Prepare to visit an urgent care facility',
        'Monitor your symptoms closely',
        'Return to emergency care if symptoms worsen'
      ]
    },
    standard: {
      title: 'Medical Attention Recommended',
      description: 'Your symptoms indicate you should see a healthcare provider soon.',
      primaryAction: {
        label: 'Book Appointment',
        icon: <Calendar className="w-5 h-5" />,
        action: () => navigate('/patient/audio-triage/book/provider-select', {
          state: {
            // Pass empty state as this component doesn't have access to triage data
            // The parent component will need to handle this when using GuidedNextSteps
            fromGuidedNextSteps: true
          }
        }),
        variant: 'default' as const
      },
      secondaryAction: {
        label: 'Message Provider',
        icon: <MessageSquare className="w-5 h-5" />,
        action: () => navigate('/patient/messages/new'),
        variant: 'outline' as const
      },
      additionalInfo: 'We recommend seeing a provider within the next 24-48 hours.',
      steps: [
        'Book an appointment with your primary care provider',
        'Take recommended over-the-counter medications if appropriate',
        'Rest and stay hydrated',
        'Monitor your symptoms and seek urgent care if they worsen'
      ]
    },
    'non-urgent': {
      title: 'Routine Care',
      description: 'Your symptoms can be addressed through routine care.',
      primaryAction: {
        label: 'Schedule Check-up',
        icon: <Calendar className="w-5 h-5" />,
        action: () => navigate('/patient/audio-triage/book/provider-select', {
          state: {
            // Pass empty state as this component doesn't have access to triage data
            // The parent component will need to handle this when using GuidedNextSteps
            fromGuidedNextSteps: true
          }
        }),
        variant: 'default' as const
      },
      secondaryAction: {
        label: 'Self-Care Guide',
        icon: <Info className="w-5 h-5" />,
        action: () => navigate('/patient/self-care'),
        variant: 'outline' as const
      },
      additionalInfo: 'Self-care measures may help. Schedule a routine appointment if symptoms persist.',
      steps: [
        'Follow recommended self-care measures',
        'Schedule a routine appointment if needed',
        'Monitor your symptoms over the next few days',
        'Return for reassessment if your condition changes'
      ]
    }
  };

  const content = guidanceContent[urgency];
  
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'border-red-300 bg-red-50';
      case 'urgent': return 'border-orange-300 bg-orange-50';
      case 'standard': return 'border-yellow-300 bg-yellow-50';
      case 'non-urgent': return 'border-green-300 bg-green-50';
      default: return 'border-gray-300 bg-gray-50';
    }
  };

  const getUrgencyTextColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'text-red-800';
      case 'urgent': return 'text-orange-800';
      case 'standard': return 'text-yellow-800';
      case 'non-urgent': return 'text-green-800';
      default: return 'text-gray-800';
    }
  };

  return (
    <Card className={cn('border-2', getUrgencyColor(urgency), className)}>
      <CardHeader>
        <CardTitle className={cn('flex items-center gap-2', getUrgencyTextColor(urgency))}>
          {urgency === 'immediate' && <AlertTriangle className="w-5 h-5" />}
          {urgency === 'urgent' && <Clock className="w-5 h-5" />}
          {urgency === 'standard' && <Stethoscope className="w-5 h-5" />}
          {urgency === 'non-urgent' && <Info className="w-5 h-5" />}
          {content.title}
        </CardTitle>
        <CardDescription className="text-base">{content.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Estimated wait time if provided */}
        {estimatedWaitTime && (
          <div className="flex items-center gap-2 text-sm bg-white p-3 rounded-md border border-gray-200">
            <Clock className="w-4 h-4 text-gray-500" />
            <span>Estimated wait time: <strong>{estimatedWaitTime}</strong></span>
          </div>
        )}

        {/* Nearest facility if provided */}
        {nearestFacility && (
          <div className="bg-white p-3 rounded-md border border-gray-200 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span className="font-medium">{nearestFacility.name}</span>
              </div>
              <span className="text-sm text-gray-500">{nearestFacility.distance}</span>
            </div>
            <div className="text-sm text-gray-600">{nearestFacility.address}</div>
            <div className="text-sm text-blue-600">{nearestFacility.phone}</div>
          </div>
        )}

        {/* Steps to follow */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700">Recommended Steps:</h4>
          <ul className="space-y-2">
            {content.steps.map((step, index) => (
              <li key={index} className="flex items-start gap-2">
                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Additional information */}
        {content.additionalInfo && (
          <div className="flex items-start gap-2 text-sm bg-white p-3 rounded-md border border-gray-200">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <span>{content.additionalInfo}</span>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row gap-3">
        <Button 
          variant={content.primaryAction.variant} 
          className="w-full flex items-center justify-center gap-2"
          onClick={content.primaryAction.action}
        >
          {content.primaryAction.icon}
          {content.primaryAction.label}
        </Button>
        <Button 
          variant={content.secondaryAction.variant} 
          className="w-full flex items-center justify-center gap-2"
          onClick={content.secondaryAction.action}
        >
          {content.secondaryAction.icon}
          {content.secondaryAction.label}
        </Button>
      </CardFooter>
    </Card>
  );
}
